import { useState } from 'react';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Button, Card, CardBody, Checkbox, Input, Label, Radio, RadioGroup, Spinner } from '@fluxora/ui';

import { isNotImplemented } from '../api/bff-client';
import { useNewOccupantMutation } from '../api/status.queries';
import {
  emptyNewOccupantForm,
  readProofFile,
  validateNewOccupantForm,
  type NewOccupantForm as NewOccupantFormState,
  type NewOccupantFormErrors,
} from '../services/new-occupant.service';

/**
 * Switch-hold self-serve resolution (E-SF07 AC1) — the New Occupant Statement +
 * proof-of-occupancy upload that creates a CRM ops case. Rendered only when the
 * status page reports a REJECTED / SWITCH_HOLD that is `resolvable`.
 *
 * All logic lives in `new-occupant.service.ts` (validation, file→base64). This
 * component renders + wires the mutation. On success it shows the case reference;
 * on a 501 it shows an HONEST "not available yet" (the CRM ops-case endpoint is
 * not wired) and never fakes a resolution.
 */
export function NewOccupantForm({ token }: { readonly token: string }): JSX.Element {
  const { t } = useTranslation();
  const submit = useNewOccupantMutation(token);
  const [form, setForm] = useState<NewOccupantFormState>(emptyNewOccupantForm);
  const [errors, setErrors] = useState<NewOccupantFormErrors>({});

  const setField = <K extends keyof NewOccupantFormState>(key: K, value: NewOccupantFormState[K]): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (file === undefined) return;
    const result = await readProofFile(file);
    if (!result.ok || result.proof === undefined) {
      setErrors((prev) => ({ ...prev, proof: result.error ?? 'shop.status.resolve.proof.required' }));
      setForm((prev) => ({ ...prev, proof: null, proofFilename: '' }));
      return;
    }
    setErrors((prev) => ({ ...prev, proof: undefined }));
    setForm((prev) => ({ ...prev, proof: result.proof ?? null, proofFilename: file.name }));
  };

  const handleSubmit = (): void => {
    const validation = validateNewOccupantForm(form);
    setErrors(validation.errors);
    if (!validation.ok || validation.value === undefined) return;
    submit.mutate(validation.value);
  };

  const err = (key: keyof NewOccupantFormErrors): JSX.Element | null =>
    errors[key] !== undefined ? <span className="text-xs text-danger">{t(errors[key] ?? '')}</span> : null;

  if (submit.isSuccess) {
    return (
      <Alert variant="success" title={t('shop.status.resolve.success.title')} role="status">
        {t('shop.status.resolve.success.body', { ref: submit.data.caseReference })}
      </Alert>
    );
  }

  return (
    <Card variant="glass" padding="md">
      <CardBody className="flex flex-col gap-4 p-0">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-medium text-foreground">{t('shop.status.resolve.title')}</h2>
          <p className="text-sm text-foreground/70">{t('shop.status.resolve.intro')}</p>
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="no-prior">{t('shop.status.resolve.priorOccupant.label')}</Label>
          <Input
            id="no-prior"
            name="priorOccupantName"
            value={form.priorOccupantName}
            onChange={(e) => setField('priorOccupantName', e.target.value)}
          />
          <span className="text-[11px] text-foreground/50">{t('shop.status.resolve.priorOccupant.note')}</span>
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="no-movein">{t('shop.status.resolve.moveInDate.label')}</Label>
          <Input
            id="no-movein"
            name="moveInDate"
            type="date"
            value={form.moveInDate}
            onChange={(e) => setField('moveInDate', e.target.value)}
          />
          {err('moveInDate')}
        </div>

        <fieldset className="flex flex-col gap-2">
          <Label>{t('shop.status.resolve.occupancyType.label')}</Label>
          <RadioGroup
            ariaLabel={t('shop.status.resolve.occupancyType.label')}
            value={form.occupancyType}
            onChange={(v) => setField('occupancyType', v === 'OWN' || v === 'RENT' || v === 'OTHER' ? v : '')}
          >
            <Radio value="OWN" label={t('shop.status.resolve.occupancyType.own')} />
            <Radio value="RENT" label={t('shop.status.resolve.occupancyType.rent')} />
            <Radio value="OTHER" label={t('shop.status.resolve.occupancyType.other')} />
          </RadioGroup>
          {err('occupancyType')}
        </fieldset>

        <div className="flex flex-col gap-1">
          <Label htmlFor="no-proof">{t('shop.status.resolve.proof.label')}</Label>
          <input
            id="no-proof"
            name="proof"
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            onChange={(e) => void handleFile(e)}
            className="text-sm text-foreground"
          />
          <span className="text-[11px] text-foreground/50">{t('shop.status.resolve.proof.note')}</span>
          {form.proofFilename !== '' ? (
            <span className="text-xs text-foreground/70">
              {t('shop.status.resolve.proof.selected', { name: form.proofFilename })}
            </span>
          ) : null}
          {err('proof')}
        </div>

        <div className="rounded-md bg-muted/40 p-3">
          <Checkbox
            name="acknowledged"
            checked={form.acknowledged}
            onChange={(e) => setField('acknowledged', e.target.checked)}
            label={t('shop.status.resolve.acknowledged.label')}
            description={t('shop.status.resolve.acknowledged.copy')}
            hasError={errors.acknowledged !== undefined}
          />
          {err('acknowledged')}
        </div>

        <Button variant="primary" onClick={handleSubmit} disabled={submit.isPending}>
          {submit.isPending ? t('shop.status.resolve.submitting') : t('shop.status.resolve.submit')}
        </Button>

        {submit.isPending ? (
          <div className="flex justify-center py-2">
            <Spinner size="sm" label={t('shop.status.resolve.submitting')} />
          </div>
        ) : null}

        {submit.isError ? (
          <Alert
            variant={isNotImplemented(submit.error) ? 'warning' : 'danger'}
            title={t('shop.status.resolve.error.title')}
            role={isNotImplemented(submit.error) ? 'status' : 'alert'}
          >
            {isNotImplemented(submit.error)
              ? t('shop.status.resolve.error.notAvailable')
              : t('shop.status.resolve.error.body')}
          </Alert>
        ) : null}
      </CardBody>
    </Card>
  );
}
